import React, { useEffect, useState } from "react";
import { useCart } from "../contexts/CartContext";
import MenuCloseButton from "./MenuCloseButton";
import { useNavigate, useSearchParams } from "react-router-dom";
import OrderItems from "./OrderItems";
import CartCrossSell from "./CartCrossSell";
import {
  FeatureToggle,
  useFeatureToggle,
} from "../contexts/FeatureToggleContext";
import { createPaymentLink } from "../api";

const Cart = () => {
  const { isCartOpen, setIsCartOpen, cart } = useCart();
  const { isFeatureEnabled, setFeatureEnabled } = useFeatureToggle();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isPaymentLinkLoading, setIsPaymentLinkLoading] = useState(false);

  const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  // Square amounts are integer cents. Items only — shipping and tax are
  // resolved server-side at checkout, hence the "Subtotal" label.
  const subtotalCents = cart.reduce(
    (sum, item) =>
      sum +
      Number(item.variation.itemVariationData?.priceMoney?.amount ?? 0) *
        item.quantity,
    0,
  );

  const handleClickToPayment = async () => {
    setIsCartOpen(false);

    // TODO: Custom flow with TaxJar
    if (isFeatureEnabled(FeatureToggle.PaymentProcess)) {
      if (isFeatureEnabled(FeatureToggle.CustomPaymentForm)) {
        navigate("/payment/checkout");
      } else {
        setIsPaymentLinkLoading(true);
        try {
          const paymentLinkResponse = await createPaymentLink(cart);
          if (paymentLinkResponse?.data && !paymentLinkResponse.error) {
            window.open(paymentLinkResponse.data.longUrl, "_blank");
          } else {
            console.error("Error creating Payment.");
          }
        } catch (error) {
          console.error("Error creating Payment.", error);
        } finally {
          setIsPaymentLinkLoading(false);
        }
      }
    } else {
      navigate("/home");
    }
  };

  useEffect(() => {
    if (searchParams.get("custompaymentform") === "enabled") {
      setFeatureEnabled(FeatureToggle.CustomPaymentForm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={`cart ${isCartOpen ? "translate-x-0" : "translate-x-full"}`}
    >
      <div className="drawer-header">
        <h2 className="drawer-title">
          Your Cart
          {itemCount > 0 && (
            <span className="ml-2 text-sm font-semibold text-gray-500 opensans-text">
              {itemCount} item{itemCount === 1 ? "" : "s"}
            </span>
          )}
        </h2>
        <MenuCloseButton onClick={() => setIsCartOpen(!isCartOpen)} />
      </div>

      <div className="drawer-body">
        {cart.length === 0 ? (
          <div className="drawer-empty">
            <div
              aria-hidden="true"
              className="flex h-16 w-16 items-center justify-center rounded-full bg-cream-dark text-2xl"
            >
              🛒
            </div>
            <h3 className="playfair-text text-xl text-brown-dark">
              Your cart is empty
            </h3>
            <p className="text-sm text-gray-500 lora-text max-w-xs">
              Add something you love and it will show up here.
            </p>
            <button
              type="button"
              className="btn-primary mt-2"
              onClick={() => setIsCartOpen(false)}
            >
              Start Shopping
            </button>
          </div>
        ) : (
          <>
            <OrderItems />
            <CartCrossSell />
          </>
        )}
      </div>

      {/* No footer on an empty cart — a disabled "Pay Now" next to the empty
          state's own Start Shopping CTA is just dead weight. */}
      {cart.length > 0 && (
        <div className="cart-footer">
          <div className="cart-total-row">
            <span className="cart-total-label">Subtotal</span>
            <span className="cart-total-value">
              ${(subtotalCents / 100).toFixed(2)}
            </span>
          </div>

          {!isFeatureEnabled(FeatureToggle.PaymentProcess) ? (
            <>
              {/* Checkout kill switch (Admin → Product Management → Site
                  Controls, or the hardcoded FeatureToggle default). Cart and
                  browsing stay fully usable; only payment is paused. */}
              <div
                role="status"
                className="mb-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
              >
                Checkout is temporarily unavailable. You can keep your items in
                your cart and check out once it’s back.
              </div>
              <button
                className="w-full py-2 px-8 rounded-lg shadow bg-gray-300 text-gray-600 cursor-not-allowed"
                disabled
              >
                Checkout unavailable
              </button>
            </>
          ) : (
            <button
              type="submit"
              className="cart-checkout-button"
              disabled={cart.length <= 0 || isPaymentLinkLoading}
              onClick={handleClickToPayment}
            >
              {isPaymentLinkLoading ? "Loading..." : "Pay Now"}
            </button>
          )}

          <button
            type="button"
            className="cart-continue-button"
            onClick={() => setIsCartOpen(false)}
          >
            Continue Shopping
          </button>
          <p className="text-center text-xs text-gray-400">
            Shipping and tax calculated at checkout.
          </p>
        </div>
      )}
    </div>
  );
};

export default Cart;
